import type { RendererObject, Tokens } from "marked";
import { escapeHtml, headingClass, type MarkdownClasses } from "./markdownClasses";

const alignClass = (align: "center" | "left" | "right" | null) => (align ? `text-${align}` : "text-left");

const renderTableRow = (cells: Tokens.TableCell[], cellClasses: string) =>
  `<tr>${cells
    .map((cell) => `<${cell.header ? "th" : "td"} class="${cellClasses} ${alignClass(cell.align)}">${cell.text}</${cell.header ? "th" : "td"}>`)
    .join("")}</tr>`;

const inlineRenderer = (classes: MarkdownClasses): RendererObject => ({
  strong({ tokens }) {
    return `<strong class="${classes.strong}">${this.parser.parseInline(tokens)}</strong>`;
  },
  em({ tokens }) {
    return `<em class="${classes.em}">${this.parser.parseInline(tokens)}</em>`;
  },
  link({ href, title, tokens }) {
    const titleAttr = title ? ` title="${escapeHtml(title)}"` : "";
    return `<a href="${escapeHtml(href)}"${titleAttr} class="${classes.link}">${this.parser.parseInline(tokens)}</a>`;
  },
  codespan({ text }) {
    return `<code class="${classes.codespan}">${escapeHtml(text)}</code>`;
  },
  image({ href, title, text }) {
    const titleAttr = title ? ` title="${escapeHtml(title)}"` : "";
    return `<img src="${escapeHtml(href)}" alt="${escapeHtml(text)}"${titleAttr} class="${classes.image}">`;
  },
});

const listRenderer = (classes: MarkdownClasses): RendererObject => ({
  list(token: Tokens.List) {
    const isTaskList = token.items.some((item) => item.task);
    const items = token.items.map((item) => this.listitem(item)).join("");
    if (isTaskList) {
      return `<ul class="${classes.taskList}">${items}</ul>`;
    }
    const tag = token.ordered ? "ol" : "ul";
    const start = token.ordered && token.start !== 1 ? ` start="${token.start}"` : "";
    return `<${tag} class="${token.ordered ? classes.ol : classes.ul}"${start}>${items}</${tag}>`;
  },
  listitem(item: Tokens.ListItem) {
    const className = item.task ? `${classes.listItem} flex items-start` : classes.listItem;
    return `<li class="${className}">${this.parser.parse(item.tokens)}</li>`;
  },
  checkbox({ checked }) {
    return `<input type="checkbox" disabled ${checked ? "checked" : ""} class="${classes.checkbox}">`;
  },
});

const blockRenderer = (classes: MarkdownClasses): RendererObject => ({
  heading({ tokens, depth }) {
    return `<h${depth} class="${headingClass(classes, depth)}">${this.parser.parseInline(tokens)}</h${depth}>`;
  },
  paragraph({ tokens }) {
    return `<p class="${classes.paragraph}">${this.parser.parseInline(tokens)}</p>`;
  },
  blockquote({ tokens }) {
    return `<blockquote class="${classes.blockquote}">${this.parser.parse(tokens)}</blockquote>`;
  },
  code({ text, lang }) {
    const langClass = lang ? ` language-${escapeHtml(lang.split(/\s+/)[0] ?? "")}` : "";
    return `<pre class="${classes.pre}"><code class="${langClass}">${escapeHtml(text)}</code></pre>`;
  },
  table(token: Tokens.Table) {
    const head = renderTableRow(token.header, classes.th);
    const rows = token.rows.map((row) => renderTableRow(row, classes.td)).join("");
    return `<table class="${classes.table}"><thead>${head}</thead><tbody>${rows}</tbody></table>`;
  },
  hr() {
    return `<hr class="${classes.hr}">`;
  },
});

export const createRenderer = (classes: MarkdownClasses): RendererObject => ({
  ...blockRenderer(classes),
  ...listRenderer(classes),
  ...inlineRenderer(classes),
});
